import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import {
  Avatar,
  Button,
  Input,
  makeStyles,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
  tokens,
} from "@fluentui/react-components";
import {
  CloudFilled,
  PersonSearchFilled,
  CheckmarkFilled,
  DismissRegular,
} from "@fluentui/react-icons";
import { usePhoneSearch, buildSfSearchRows } from "../hooks/usePhoneSearch";
import { useMicrosoftSearch } from "../hooks/useMicrosoftSearch";
import PhoneSearchDropdown from "./PhoneSearchDropdown";
import { getSfObjectsForPhoneBookSearch } from "../utils/callCenterSettings";

const MIN_SF_CHARS = 2;
const MIN_MS_CHARS = 3;
const DEBOUNCE_MS = 300;
// Inputs that start with a dialpad character are treated as a phone number
// being dialled — phonebook search is suppressed entirely in this case.
const DIALPAD_PREFIX_RE = /^[0-9*#+]/;
const LISTBOX_ID = "phone-search-listbox";

const SOURCE = {
  SALESFORCE: "salesforce",
  MICROSOFT: "microsoft",
};

const useStyles = makeStyles({
  inputArea: {
    position: "relative",
    width: "96%",
    marginBottom: "2px",
  },
  sourceBtn: {
    minWidth: 0,
    padding: "2px 4px",
    color: "#0f6cbd",
  },
  // ── Selected MS contact token view ────────────────────────────
  selectedTagWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    paddingLeft: "2px",
    paddingRight: "4px",
    paddingTop: "4px",
    paddingBottom: "4px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
    cursor: "default",
    ":focus-within": {
      borderBottom: `2px solid ${tokens.colorBrandStroke1}`,
    },
  },
  selectedTagName: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  selectedTagDismiss: {
    flexShrink: 0,
    minWidth: "0 !important",
    padding: "2px 4px",
    color: tokens.colorNeutralForeground3,
  },
  selectedTagAvatar: {
    flexShrink: 0,
  },
});

/**
 * PhoneSearchInput
 *
 * Controlled input combining a text field with Salesforce + Microsoft 365
 * phonebook search. Parent owns the raw input value via `value` / `onChange`
 * and is notified of a record selection via `onSelect(row | null)`.
 *
 * Props:
 *   value          – current text value (controlled)
 *   onChange       – callback(newValue: string)
 *   onSelect       – callback(row | null) when a result is picked / cleared
 *                    row: { recordId, recordType, name, company, phone, label, source }
 *   onSourceChange – optional callback(source) when the user switches source
 *   placeholder    – optional placeholder (defaults based on active source)
 *   disabled       – disables the input
 *   style/className– forwarded to the outer container
 */
export default function PhoneSearchInput({
  value,
  onChange,
  onSelect,
  onSourceChange,
  placeholder,
  disabled = false,
  style,
  className,
}) {
  const styles = useStyles();

  // ── State ─────────────────────────────────────────────────────────
  const sfAvailable = getSfObjectsForPhoneBookSearch().length > 0;
  const [source, setSource] = useState(SOURCE.MICROSOFT);
  const [sfSearchQuery, setSfSearchQuery] = useState(""); // committed on Enter / search icon
  const [msSearchQuery, setMsSearchQuery] = useState(""); // live on every keystroke
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedMSRow, setSelectedMSRow] = useState(null);

  // ── Refs ──────────────────────────────────────────────────────────
  const containerRef = useRef(null);
  // The most recent value this component emitted via onChange. Used to tell
  // an "external" value change (e.g. dialpad) apart from our own updates.
  const lastEmittedValueRef = useRef(value);

  // Latest-callback ref so effects don't re-run when parent passes
  // a non-memoized onSelect.
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  });

  // ── Derived / routed queries ─────────────────────────────────────
  const sfQuery = source === SOURCE.SALESFORCE ? sfSearchQuery : "";
  const msQuery = source === SOURCE.MICROSOFT ? msSearchQuery : "";

  const {
    results: sfResults,
    isLoading: sfLoading,
    error: sfError,
    hasSearched: sfHasSearched,
  } = usePhoneSearch(sfQuery);

  const {
    rows: msRowsRaw,
    isLoading: msLoading,
    error: msError,
    hasSearched: msHasSearched,
  } = useMicrosoftSearch(msQuery);

  // ── Row normalisation ────────────────────────────────────────────
  const sfRows = useMemo(
    () =>
      buildSfSearchRows(sfResults).map((r) => ({ ...r, source: SOURCE.SALESFORCE })),
    [sfResults],
  );

  // Deduplicate MS rows by recordId — one entry per user (Teams ID used for calling)
  const msRows = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const row of msRowsRaw) {
      if (seen.has(row.recordId)) continue;
      seen.add(row.recordId);
      out.push(row);
    }
    return out;
  }, [msRowsRaw]);

  const isSF = source === SOURCE.SALESFORCE;
  const searchRows = isSF ? sfRows : msRows;
  const isLoading = isSF ? sfLoading : msLoading;
  const activeError = isSF ? sfError : msError;

  const showDropdown = isSF
    ? sfSearchQuery.length >= MIN_SF_CHARS && (sfLoading || sfHasSearched)
    : msSearchQuery.length >= MIN_MS_CHARS && (msLoading || msHasSearched);

  const activePlaceholder =
    placeholder ??
    (isSF ? "Type name or phone number" : "Search Microsoft Office 365 users");

  // ── Helpers ───────────────────────────────────────────────────────
  const resetSearch = useCallback(() => {
    setSfSearchQuery("");
    setMsSearchQuery("");
    setActiveIndex(-1);
  }, []);

  // Clears any MS selection and notifies the parent exactly once.
  const clearMSSelection = useCallback(() => {
    setSelectedMSRow((prev) => {
      if (prev) onSelectRef.current?.(null);
      return null;
    });
  }, []);

  // ── Effects ───────────────────────────────────────────────────────
  // Reset highlight when the result set changes
  useEffect(() => {
    setActiveIndex(-1);
  }, [searchRows]);

  // Detect EXTERNAL value changes (dialpad, parent reset, etc.) and clear
  // any in-progress search/selection. Internal changes are recognised by
  // matching lastEmittedValueRef.
  useEffect(() => {
    if (value === lastEmittedValueRef.current) return;
    lastEmittedValueRef.current = value;
    resetSearch();
    clearMSSelection();
  }, [value, resetSearch, clearMSSelection]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        resetSearch();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [resetSearch]);

  // Debounced auto-search — shared for both sources.
  // Suppressed when an MS user is already selected or the input looks like a
  // dialled phone number (starts with a dialpad character).
  useEffect(() => {
    if (selectedMSRow || DIALPAD_PREFIX_RE.test(value)) {
      setSfSearchQuery("");
      setMsSearchQuery("");
      return;
    }
    const minLen = isSF ? MIN_SF_CHARS : MIN_MS_CHARS;
    if (value.length < minLen) {
      setSfSearchQuery("");
      setMsSearchQuery("");
      return;
    }
    const id = setTimeout(() => {
      if (isSF) {
        setSfSearchQuery(value);
        setMsSearchQuery("");
      } else {
        setMsSearchQuery(value);
        setSfSearchQuery("");
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [value, isSF, selectedMSRow]);

  // ── Handlers ──────────────────────────────────────────────────────
  const emitChange = useCallback(
    (next) => {
      lastEmittedValueRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  const handleSourceChange = useCallback(
    (newSource) => {
      if (newSource === source) return;
      resetSearch();
      clearMSSelection();
      setSource(newSource);
      onSourceChange?.(newSource);
    },
    [source, resetSearch, clearMSSelection, onSourceChange],
  );

  const handleSelectRow = useCallback(
    (row) => {
      if (row.source === SOURCE.MICROSOFT) {
        // Show display name; parent uses Teams ID for the actual call
        emitChange(row.name);
        setSelectedMSRow(row);
      } else {
        // Salesforce: surface the phone number in the input
        emitChange(row.phone);
        setSelectedMSRow(null);
      }
      resetSearch();
      onSelect(row);
    },
    [emitChange, resetSearch, onSelect],
  );

  const handleClearMSRow = useCallback(() => {
    emitChange("");
    setSelectedMSRow(null);
    resetSearch();
    onSelect(null);
  }, [emitChange, resetSearch, onSelect]);

  const handleChange = useCallback(
    (_e, data) => {
      emitChange(data.value);
      clearMSSelection();
      // Queries are committed by the debounce effect; reset highlight only.
      setActiveIndex(-1);
    },
    [emitChange, clearMSSelection],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        if (showDropdown && activeIndex >= 0) {
          e.preventDefault();
          const row = searchRows[activeIndex];
          if (row) handleSelectRow(row);
        }
        return;
      }
      if (!showDropdown) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, searchRows.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
      } else if (e.key === "Escape") {
        resetSearch();
      }
    },
    [showDropdown, activeIndex, searchRows, handleSelectRow, resetSearch],
  );

  const handleTagKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" || e.key === "Delete" || e.key === "Backspace") {
        handleClearMSRow();
      }
    },
    [handleClearMSRow],
  );

  // ── Source selector menu (shared between search and selected modes) ──
  const sourceMenu = (
    <Menu positioning={{ position: "below-start" }}>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          size="small"
          icon={isSF ? <CloudFilled /> : <PersonSearchFilled />}
          title={
            isSF ? "Search source: Salesforce" : "Search source: Microsoft"
          }
          className={styles.sourceBtn}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem
            icon={
              !isSF ? (
                <CheckmarkFilled style={{ color: "#0f6cbd" }} />
              ) : (
                <PersonSearchFilled />
              )
            }
            onClick={() => handleSourceChange(SOURCE.MICROSOFT)}
          >
            Microsoft Search
          </MenuItem>
          {sfAvailable && (
            <MenuItem
              icon={
                isSF ? (
                  <CheckmarkFilled style={{ color: "#0f6cbd" }} />
                ) : (
                  <CloudFilled />
                )
              }
              onClick={() => handleSourceChange(SOURCE.SALESFORCE)}
            >
              Salesforce Search
            </MenuItem>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );

  return (
    <div
      ref={containerRef}
      className={`${styles.inputArea}${className ? ` ${className}` : ""}`}
      style={style}
    >
      {selectedMSRow ? (
        /* ── Selected MS user — token / tag view ──────────────────── */
        <div
          className={styles.selectedTagWrapper}
          role="group"
          aria-label={`Selected contact: ${selectedMSRow.name}`}
          onKeyDown={handleTagKeyDown}
        >
          {sourceMenu}
          <Avatar
            name={selectedMSRow.name}
            size={20}
            color="brand"
            aria-hidden="true"
            className={styles.selectedTagAvatar}
          />
          <Text className={styles.selectedTagName} title={selectedMSRow.name}>
            {selectedMSRow.name}
          </Text>
          <Button
            appearance="transparent"
            size="small"
            icon={<DismissRegular />}
            title="Clear selection (Esc)"
            className={styles.selectedTagDismiss}
            onClick={handleClearMSRow}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={`Remove ${selectedMSRow.name}`}
          />
        </div>
      ) : (
        /* ── Normal search input ──────────────────────────────────── */
        <Input
          style={{ width: "100%" }}
          appearance="underline"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={activePlaceholder}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? LISTBOX_ID : undefined}
          aria-activedescendant={
            showDropdown && activeIndex >= 0
              ? `${LISTBOX_ID}-option-${activeIndex}`
              : undefined
          }
          contentBefore={sourceMenu}
        />
      )}

      {showDropdown && (
        <PhoneSearchDropdown
          id={LISTBOX_ID}
          rows={searchRows}
          activeIndex={activeIndex}
          isLoading={isLoading}
          error={activeError}
          onSelect={handleSelectRow}
          onHoverIndex={setActiveIndex}
          onClose={resetSearch}
        />
      )}
    </div>
  );
}
